import { Button } from '@thanx/uikit/button'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { contrastRatio, isHexColor } from 'utilities/contrast'
import { TOUR_ANCHORS } from './tourAnchors'
import { useMerchantBrand } from './useMerchantBrand'

const t = buildTranslate('recovery_ai.sandbox')

// WCAG 1.4.11 (non-text contrast, 3:1) guard for the send glyph: brandColor
// is the merchant's unvalidated `primary_color` string, so a light brand can
// drop a white glyph below 3:1 against its own fill. Pick white when it still
// clears 3:1, grey100 otherwise. `isHexColor` guards `contrastRatio`, which
// can't measure a non-hex value (named color, rgb()) — fall back to the
// current white rather than mis-parsing it.
export function pickSendGlyphColor(brandColor: string): string {
  if (!isHexColor(brandColor)) return merchantTheme.colors.white
  return contrastRatio(merchantTheme.colors.white, brandColor) >= 3
    ? merchantTheme.colors.white
    : merchantTheme.colors.grey100
}

// ENG-979: the send control sits INSIDE the text box (bottom-right), so the
// textarea reserves a gutter wide enough for the button plus its inset.
const SEND_BUTTON_SIZE_PX = 24
// Bottom-anchored rather than centred, so the button stays put as the textarea grows.
const SEND_BUTTON_INSET_Y_PX = 5
const SEND_BUTTON_INSET_X_PX = 8
const TEXTAREA_SEND_GUTTER_PX = SEND_BUTTON_SIZE_PX + SEND_BUTTON_INSET_X_PX * 2

// Send-area geometry, measured off the Figma (plan §1.2): a 1px rule, ~9px of air, a
// 36px box, then ~19px below it — 65px in total against the 92px that shipped.
// The extra 27px was mostly the placeholder: "Type a message as the customer..."
// wrapped to two lines in the phone's 210px of usable width and pushed the box from
// its 42px resting height to 59px. `chat_placeholder` is now short enough to sit on
// one line (D2) and this height budget assumes it stays that way.
const FORM_PADDING = '9px 12px 19px'
const TEXTAREA_PADDING_Y_PX = 7
const TEXTAREA_LINE_HEIGHT_PX = 20
// One line at rest (7 + 20 + 7 = 34, +2px border = a 36px box). The textarea still
// grows to maxHeight as the merchant types — 36px is the RESTING height, not a cap.
const TEXTAREA_MIN_HEIGHT_PX =
  TEXTAREA_LINE_HEIGHT_PX + TEXTAREA_PADDING_Y_PX * 2

type Props = {
  disabled: boolean
  onSend: (text: string) => void
  // AI-583: when the conversation is terminal (a reward was granted), blank the
  // visible placeholder so the greyed box reads as "done" instead of inviting
  // input. Distinct from `disabled`, which is also true during the transient
  // awaitingResponse (there the placeholder stays). The aria-label is unchanged
  // so the control remains labeled for assistive tech.
  terminal?: boolean
}

const ChatInput = ({ disabled, onSend, terminal }: Props) => {
  const [css] = useStyletron()
  const [value, setValue] = useState('')
  // ENG-979: the send button is merchant-branded (red for the Figma's PizzaCo),
  // not the fixed Thanx coral — the phone mock previews what the CUSTOMER sees.
  const { color: brandColor } = useMerchantBrand()
  const sendGlyphColor = pickSendGlyphColor(brandColor)

  // Re-enabling a disabled DOM input does not restore focus on its own, so the
  // merchant testing a scenario has to re-click the box after every agent
  // reply. Only refocus on the disabled -> enabled transition (never on mount,
  // which would steal focus when a wizard step first renders).
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const wasDisabled = useRef(disabled)
  useEffect(() => {
    if (wasDisabled.current && !disabled) {
      textareaRef.current?.focus()
    }
    wasDisabled.current = disabled
  }, [disabled])

  const submit = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    onSend(trimmed)
    setValue('')
  }, [value, disabled, onSend])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      submit()
    },
    [submit]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        submit()
      }
    },
    [submit]
  )

  return (
    <form
      data-tour={TOUR_ANCHORS.chatInput}
      onSubmit={handleSubmit}
      className={css({
        borderTop: `1px solid ${merchantTheme.colors.grey20}`,
        padding: FORM_PADDING,
      })}
    >
      {/* Terminal-state cue for assistive tech (AI-583 a11y): the greyed box is
          visual-only, and disabling the textarea drops it from the tab order, so
          a screen-reader user would find the compose affordance silently gone.
          Always-mounted polite live region announces the terminal state on the
          flip while the greyed box stays the only VISIBLE cue ("no visible
          message" ≠ no accessible message).
            context: the old `!isLocked` guard existed because a scripted demo is
            terminal from its first frame (ChatPanel's `conversationEnded` takes
            scriptedDemo as its first disjunct), so this would have announced
            "complete" on top of the demo's own visible caption. Both are gone —
            the demo no longer renders this component at all — so the announcement
            is unconditional again, which is what the live terminal state wants. */}
      <div role="status" className="sr-only">
        {terminal ? t('conversation_ended') : ''}
      </div>
      {/* ENG-979: the box IS the compose control — the send button is positioned
          inside it (bottom-right) rather than sitting detached beside it, so the
          border/radius move from the textarea onto this wrapper and the textarea
          itself renders borderless. */}
      {/* The Figma box is FILLED at rest (grey05), where this used to be white and
          only greyed on `disabled`. That would have flattened AI-583's terminal cue to
          nothing, so the disabled fill drops a step to grey10 instead of being dropped:
          the state delta survives, one token quieter.
            context: the fill was never the whole cue anyway — the blanked placeholder
            and the greyed-out send button carry it too, and the sr-only live region
            above is what actually reaches assistive tech. */}
      <div
        className={css({
          position: 'relative',
          border: `1px solid ${merchantTheme.colors.grey30}`,
          borderRadius: '4px',
          backgroundColor: disabled
            ? merchantTheme.colors.grey10
            : merchantTheme.colors.grey05,
        })}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          // AI-583: honor `disabled` on the textarea itself (not just the send
          // button) so a granted reward locks the compose box — the greyed box is
          // the cue that the conversation reached its terminal state.
          disabled={disabled}
          placeholder={terminal ? '' : t('chat_placeholder')}
          // NOT chat_placeholder: that string is blanked in the terminal state and is
          // deliberately terse, so the accessible name is its own key and survives it
          // (WCAG 4.1.2 — the control stays named either way).
          aria-label={t('chat_input_label')}
          className={css({
            display: 'block',
            width: '100%',
            minHeight: `${TEXTAREA_MIN_HEIGHT_PX}px`,
            maxHeight: '120px',
            padding: `${TEXTAREA_PADDING_Y_PX}px 12px`,
            // Keep the typed text clear of the inset send button.
            paddingRight: `${TEXTAREA_SEND_GUTTER_PX}px`,
            border: 'none',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            fontSize: '14px',
            lineHeight: `${TEXTAREA_LINE_HEIGHT_PX}px`,
            fontFamily: 'Lato, sans-serif',
            resize: 'none',
            color: merchantTheme.colors.grey90,
            ':focus': { outline: 'none' },
            ':disabled': {
              color: merchantTheme.colors.grey50,
              cursor: 'not-allowed',
            },
          })}
        />
        {/* Send is merchant-branded (ENG-979): the phone mock previews what the
            CUSTOMER sees, so the button takes the merchant's primary color and
            falls back to the Thanx coral when none is configured. uikit spreads a
            consumer BaseButton override LAST (replacing its kind preset), so the
            full button styling is supplied here. */}
        <Button
          type="submit"
          kind="primary"
          disabled={!value.trim() || disabled}
          aria-label={t('send_message')}
          overrides={{
            BaseButton: {
              style: {
                position: 'absolute',
                right: `${SEND_BUTTON_INSET_X_PX}px`,
                bottom: `${SEND_BUTTON_INSET_Y_PX}px`,
                width: `${SEND_BUTTON_SIZE_PX}px`,
                height: `${SEND_BUTTON_SIZE_PX}px`,
                minWidth: `${SEND_BUTTON_SIZE_PX}px`,
                backgroundColor: brandColor,
                color: sendGlyphColor,
                borderTopLeftRadius: '6px',
                borderTopRightRadius: '6px',
                borderBottomLeftRadius: '6px',
                borderBottomRightRadius: '6px',
                paddingTop: '0',
                paddingBottom: '0',
                paddingLeft: '0',
                paddingRight: '0',
                fontSize: '12px',
                ':hover': { backgroundColor: brandColor },
                ':disabled': {
                  backgroundColor: merchantTheme.colors.grey30,
                  color: merchantTheme.colors.white,
                },
              },
            },
          }}
        >
          <i className="fa fa-arrow-up" aria-hidden="true" />
        </Button>
      </div>
    </form>
  )
}

export default ChatInput
